import {
  Component,
  computed,
  inject,
  Signal,
  signal,
  Type,
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { TPipe, TRANSLATION_PREFIX } from '@pipes/t.pipe';
import {
  DateAdapter,
  MAT_DATE_FORMATS,
  MAT_DATE_LOCALE,
} from '@angular/material/core';
import { MatInputModule } from '@angular/material/input';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatChipsModule } from '@angular/material/chips';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { AbbCommonModule } from 'app/abb-common.module';
import { MomentDateAdapter } from '@angular/material-moment-adapter';
import {
  combineDateTimeLocaleFromISO,
  DATE_LOCALE_GB_FORMAT,
  formatDate,
} from '@utils/datetime';
import { DashboardEditComponent } from '@pages/dashboards/dashboard-edit/dashboard-edit.component';
import { Apollo } from 'apollo-angular';
import { reportTemplateQuery } from '@graphql/report';
import { filter, firstValueFrom, map } from 'rxjs';
import { AbbQueryComponent } from '@components/abb-query/abb-query.component';
import {
  PropertyListQuery,
  PropertyListQueryVariables,
  ReportTemplateListQuery,
  UiSettings,
} from 'gql/graphql';
import { identity } from '@utils/functions';
import { CustomWidget } from '@pages/dashboards/widgets/widget';
import { TranslateService } from '@ngx-translate/core';
import { ActivatedRoute } from '@angular/router';
import { OverlayService } from '@services/ui/overlay.service';
import { noop } from '@utils/runtime';
import { t } from '@utils/strings';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { CognitoService } from '@services/cognito/cognito.service';
import { UserSettingsService } from '@services/user/user-settings.service';
import { savePdfReport } from '@graphql/dashboard';
import { ToastService } from '@services/ui/toast.service';
import { propertyListQuery } from '@graphql/property';

@Component({
  selector: 'abb-report-template-print-page',
  standalone: true,
  providers: [
    {
      provide: TRANSLATION_PREFIX,
      useValue: 'reports',
    },
    {
      provide: DateAdapter,
      useClass: MomentDateAdapter,
      deps: [MAT_DATE_LOCALE],
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_LOCALE_GB_FORMAT },
  ],
  template: `
    <!-- Background gradient per la pagina -->
    <div
      class="min-h-screen bg-gradient-to-br from-slate-50 via-gray-50 to-blue-50"
    >
      <abb-query
        errorClass="mt-32"
        loadingClass="mt-32"
        [query]="reportQuery()"
        [successTemplate]="successTemplate"
      />

      <ng-template #successTemplate let-report>
        @if (asReport(report); as report){

        <div class="fixed top-6 right-6 z-50">
          <abb-button
            class="flex flex-row items-center"
            (onclick)="onExport()"
            [disabled]="exporting()"
            data-html2canvas-ignore="true"
          >
            {{ 'Export PDF' | t }}
          </abb-button>
        </div>
        <div
          class="flex flex-col justify-center items-center px-4 py-8 min-h-screen"
        >
          <div
            id="custom-template"
            class="min-h-[90vh] w-[50%] relative bg-white rounded-2xl shadow-2xl
           border border-gray-100 backdrop-blur-sm
           my-auto"
          >
            <div class="relative z-10 px-8 pt-8 mb-0 ">
              <div class="absolute inset-0 rounded-t-2xl"></div>

              <div class="relative z-10">
                <div
                  class="flex justify-end items-center mb-6"
                  data-html2canvas-ignore="true"
                >
                  <div class="flex-shrink-0">
                    @if (uiSettings()?.logo?.custom || uiSettings()?.logo?.abb)
                    {
                    <img
                      [src]="
                        uiSettings()?.logo?.custom?.toString() ||
                        '/assets/images/ABB_logo.png'
                      "
                      alt="Company Logo"
                      class="h-8 w-auto object-contain"
                    />
                    }
                  </div>
                </div>

                <div
                  data-html2canvas-ignore="true"
                  class="flex justify-between items-end"
                >
                  <div class="flex flex-col text-left mx-4">
                    <span
                      class="text-sm/8 font-semibold text-abb-primary leading-none p-0 m-0"
                      >—</span
                    >
                    <h3 class="text-sm/8 text-gray-400 -mb-1">CUSTOM REPORT</h3>
                    <h2 class="text-sm/8 font-semibold text-gray-700">
                      {{ reportTitle }}
                    </h2>
                  </div>
                  <div class="text-xs text-gray-600">
                    <div
                      class="grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-left"
                    >
                      <div><strong>Report Generated on:</strong></div>
                      <div>{{ getCurrentDate() }}</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div class="px-4 space-y-6 pb-8">
              @for (widget of report.widgets; track widget) { @if
              (componentMap[widget.type][widget.display]; as component) {
              <div
                class="group relative bg-gradient-to-br from-white to-gray-50/50 p-6 transition-all duration-300 break-inside-avoid rounded-lg
                     "
              >
                <div class="relative z-10 h-full">
                  <ng-container
                    *ngComponentOutlet="
                      component;
                      inputs: {
                        dashboardId: 'report',
                        reportParams: {
                          id: report.id
                        },
                        widget,
                        refreshRateMinutes: 60
                      }
                    "
                  ></ng-container>
                </div>
              </div>
              } }
            </div>
          </div>
        </div>
        }
      </ng-template>
    </div>
  `,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
    MatChipsModule,
    MatDatepickerModule,
    AbbCommonModule,
    AbbQueryComponent,
    TPipe,
  ],
  styles: `
    @page {
      size: 210mm 297mm;
    }
    .a4 {
      padding: 12mm 8mm;
      width: 210mm;
    }`,
})
export class ReportTemplatePrintPageComponent {
  getFormattedFromDate() {
    return formatDate(this.fromDateLocale(), this.translate.currentLang, true);
  }

  getFormattedToDate() {
    return formatDate(this.toDateLocale(), this.translate.currentLang, true);
  }

  getCurrentDate() {
    return formatDate(new Date(), this.translate.currentLang, true);
  }

  widgetData(a: any) {
    return {
      widgetId: a.widgetType,
      type: a.widgetType,
      ...JSON.parse(a.widgetConfig),
    };
  }
  parseJSON = JSON.parse;

  asReport = identity<ReportTemplateListQuery['reportTemplateList'][number]>;

  fromDateLocale = computed(() => combineDateTimeLocaleFromISO(this.fromDate));
  toDateLocale = computed(() => combineDateTimeLocaleFromISO(this.toDate));

  reportQuery = signal<any>(undefined);
  componentMap = DashboardEditComponent.COMPONENT_MAP as Record<
    string,
    Record<string, Type<CustomWidget>>
  >;
  exporting = signal(false);
  route = inject(ActivatedRoute);
  prefix = inject(TRANSLATION_PREFIX);
  uiSettings: Signal<UiSettings | null>;
  reportTitle = decodeURIComponent(this.route.snapshot.params['reportTitle']);
  fromDate = new Date(this.route.snapshot.params['fromDate']);
  toDate = new Date(this.route.snapshot.params['toDate']);
  // properties = this.route.snapshot.params['properties']
  //   .split(',')
  //   .map((p: string) => decodeURIComponent(p));
  // propertyList = signal<PropertyListQuery['propertyList']>([]);

  // propertyNames = computed(() => {
  //   return this.properties
  //     .map((id: string) => this.propertyList().find((p) => p.id === id)?.name)
  //     .filter((name: string): name is string => !!name);
  // });

  constructor(
    private readonly apollo: Apollo,
    private readonly translate: TranslateService,
    private readonly overlay: OverlayService,
    private readonly userService: UserSettingsService,
    private readonly toast: ToastService,
    private readonly cognitoService: CognitoService
  ) {
    this.uiSettings = this.userService.uiSettings;
    // apollo
    //   .watchQuery<PropertyListQuery, PropertyListQueryVariables>({
    //     query: propertyListQuery,
    //   })
    //   .valueChanges.subscribe({
    //     next: ({ data }) => {
    //       this.propertyList.set(data.propertyList);
    //     },
    //     error: (err) => {
    //       this.toast.notifyError(getErrorMessage(err));
    //     },
    //   });
  }

  async ngOnInit() {
    this.reportQuery.set(
      firstValueFrom(
        this.apollo
          .watchQuery({
            variables: {
              id: decodeURIComponent(this.route.snapshot.params['reportId']),
            },
            query: reportTemplateQuery,
          })
          .valueChanges.pipe(
            filter(({ data, loading }) => !loading && !!data?.reportTemplate),
            map(({ data }) => data.reportTemplate)
          )
      )
    );
  }

  async onExport() {
    this.exporting.set(true);
    try {
      await this.createPDF();
      this.exporting.set(false);
      this.toast.notifySuccess(
        t(this.prefix, `File generated successfully`, this.translate)
      );
    } catch (err) {
      this.exporting.set(false);
      console.error(err);
      this.toast.notifyError(
        t(
          this.prefix,
          `An error occurred while generating export file`,
          this.translate
        )
      );
    }
  }

  private async createPDF() {
    const doc = new jsPDF();
    const pdfMarginTop = 20;
    const pdfMarginLeft = 20;
    const pdfMarginRight = 20;
    const paragraphSpacing = 8;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    let yPosition = pdfMarginTop;

    let logoPath =
      this.uiSettings()?.logo?.custom?.toString() ??
      '/assets/images/ABB_logo.png';
    const img = new Image();
    img.src = logoPath;

    await new Promise((resolve) => (img.onload = resolve));

    const maxLogoWidth = 20;
    const maxLogoHeight = 10;
    const aspectRatio = img.width / img.height;
    const logoWidth =
      aspectRatio > maxLogoWidth / maxLogoHeight
        ? maxLogoWidth
        : aspectRatio * maxLogoHeight;
    const logoHeight =
      aspectRatio > maxLogoWidth / maxLogoHeight
        ? (1 / aspectRatio) * maxLogoWidth
        : maxLogoHeight;

    // Calcola la posizione x per allineare a destra
    const xPosition = pageWidth - logoWidth - pdfMarginRight;

    doc.addImage(logoPath, 'PNG', xPosition, yPosition, logoWidth, logoHeight);

    // Sposta la y position sotto il logo
    yPosition += logoHeight + paragraphSpacing;

    // Sezione titolo e data allineati orizzontalmente
    const currentDate = new Date().toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });

    // Carattere "—" sopra CUSTOM REPORT (text-abb-primary)
    doc.setFontSize(16);
    doc.setTextColor(255, 0, 15);
    doc.text('—', pdfMarginLeft, yPosition);

    yPosition += 6;

    // "DASHBOARD REPORT" sotto il carattere
    doc.setFontSize(8);
    doc.setTextColor(150);
    doc.text('DASHBOARD REPORT', pdfMarginLeft, yPosition);

    yPosition += 5;

    // Titolo principale
    doc.setFontSize(16);
    doc.setTextColor(60);
    doc.text(this.reportTitle, pdfMarginLeft, yPosition);

    // Data a destra (sulla stessa altezza del titolo principale)
    doc.setFontSize(10);
    doc.setTextColor(100); // Grigio per la data
    const dateText = `Report Generated on: ${currentDate}`;
    const dateWidth = doc.getTextWidth(dateText);
    const dateXPosition = pageWidth - dateWidth - pdfMarginRight;
    doc.text(dateText, dateXPosition, yPosition);

    yPosition += paragraphSpacing * 2; // Spazio extra prima del contenuto

    const dashboard = document.getElementById('custom-template');
    if (dashboard) {
      const canvas = await html2canvas(dashboard, {
        scale: 1.5,
        useCORS: true,
        allowTaint: true,
        logging: false,
        windowWidth: dashboard.scrollWidth,
        windowHeight: dashboard.scrollHeight,
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.8);

      const availableWidth = pageWidth - pdfMarginLeft * 2;
      const availableHeight = pageHeight - yPosition - pdfMarginTop;

      const imgAspectRatio = canvas.width / canvas.height;
      let imageWidth = availableWidth;
      let imageHeight = imageWidth / imgAspectRatio;

      if (imageHeight > availableHeight) {
        imageHeight = availableHeight;
        imageWidth = imageHeight * imgAspectRatio;
      }

      const imgXPosition = pdfMarginLeft + (availableWidth - imageWidth) / 2;

      doc.addImage(
        imgData,
        'JPEG',
        imgXPosition,
        yPosition,
        imageWidth,
        imageHeight
      );

      const pdfBase64 = doc.output('datauristring');
      const name = `${this.reportTitle}`;
      const createdAt = new Date().toISOString();
      const userId = this.cognitoService.loggedInUser()?.id;

      await firstValueFrom(
        this.apollo.mutate({
          mutation: savePdfReport,
          variables: {
            name,
            createdAt,
            userId: userId?.substring(2),
            content: pdfBase64,
          },
        })
      );

      doc.save(`${this.reportTitle}.pdf`);
    }
  }
}
